import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { MemberPortalOrganization } from './MemberSignIn'
import { organizationSection } from './portalOrganizationSections'
import { portalDashboardKeys, type PortalDashboardKey } from './portalDashboard.types'

export const portalOrganizationTabs = [
  { key: 'profile', label: 'Profile', section: 'profile' },
  { key: 'membership', label: 'Membership', section: 'dashboard' },
  { key: 'people', label: 'People', section: 'dashboard' },
  { key: 'groups', label: 'Groups', section: 'dashboard' },
  { key: 'engagement', label: 'Engagement', section: 'dashboard' },
  { key: 'transactions', label: 'Transactions', section: 'dashboard' },
  { key: 'roles', label: 'Manage roles', section: 'roles' },
] as const
export type PortalOrganizationTab = (typeof portalOrganizationTabs)[number]['key']
const listParams = ['person', 'q', 'cursor', 'status', 'level', 'categories', 'sort']

export function usePortalDashboardSelection(organization: MemberPortalOrganization) {
  const [params, setParams] = useSearchParams()
  const canManage = organizationSection(organization, 'roster')
  const tabs = portalOrganizationTabs.filter((item) =>
    organizationSection(organization, item.section),
  )
  // Links from before the tabbed page used tab=dashboard for the tiles.
  const requested = params.get('tab') === 'dashboard' ? 'engagement' : params.get('tab')
  const tab: PortalOrganizationTab =
    tabs.find((item) => item.key === requested)?.key ?? 'engagement'
  const selected = portalDashboardKeys.find((key) => key === params.get('tile')) ?? 'spend'
  useEffect(() => {
    const next = new URLSearchParams(params)
    if (tab === 'engagement') next.set('tile', selected)
    if (params.get('tab') && params.get('tab') !== tab) next.set('tab', tab)
    if (next.toString() !== params.toString()) setParams(next, { replace: true })
  }, [params, selected, tab, setParams])
  const select = (changes: Record<string, string>) => {
    const next = new URLSearchParams(params)
    listParams.forEach((key) => next.delete(key))
    Object.entries(changes).forEach(([key, value]) => next.set(key, value))
    setParams(next)
  }
  return {
    selected,
    tab,
    tabs,
    canManage,
    people: true,
    selectTile: (tile: PortalDashboardKey) =>
      // The Courses completed tile opens the courses list on completed courses.
      select(tile === 'courses_completed' ? { tile, status: 'completed' } : { tile }),
    selectTab: (next: string) => select({ tab: next }),
  }
}
